import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiFetch, ApiClientError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import type { PartnerTierAssignInput, PartnerTierAssignmentRow } from "@colab/shared";
import type { PartnerRow } from "@/features/partners/use-partners";

export type AssignPartnerTierInput = PartnerTierAssignInput & {
  // Optional optimistic-patch hint. Pass the tier's `legacy_tier` slug (e.g.
  // "gold") ONLY when assigning a partner in the DEFAULT program — that's
  // the single program the 0038 tg_sync_partner_legacy_tier trigger mirrors
  // into partners.tier. Skipped → no optimistic patch on partner caches
  // (the onSettled invalidate still refetches them).
  patchPartnerLegacyTier?: string;
};

type AssignResponse = { id: string; assignment: PartnerTierAssignmentRow };

type Context = {
  previousDetail: PartnerRow | undefined;
  previousList: PartnerRow[] | undefined;
};

// POST /api/admin/tiers/assignments
//
// Cross-key invalidation surface — the bucket-7 marquee pattern. Tier
// assignments ripple to:
//
//   - `tiers.assignmentsForProgram(program_id)` — the program's assignments
//     listing
//   - `partners.detail(partner_id)` — the partner detail view (tier badge)
//   - `partners.list()` — the partner grid (tier column)
//
// We optimistically patch `partners.detail` + `partners.list` only when the
// caller passes `patchPartnerLegacyTier` (i.e. assigning in the default
// program — the only case where partners.tier actually changes). The
// onSettled invalidate is unconditional so the server is always the source
// of truth.
//
// Pattern C across three keys. Documents the convention for bucket 8
// (notifications) and later buckets that touch cross-entity state.
export function useAssignPartnerTier() {
  const queryClient = useQueryClient();
  return useMutation<AssignResponse, ApiClientError, AssignPartnerTierInput, Context>({
    mutationFn: ({ patchPartnerLegacyTier: _ignored, ...input }) =>
      apiFetch<AssignResponse>("/admin/tiers/assignments", {
        method: "POST",
        body: JSON.stringify(input),
      }),
    onMutate: async ({ partner_id, patchPartnerLegacyTier }) => {
      const detailKey = queryKeys.partners.detail(partner_id);
      const listKey = queryKeys.partners.list();
      await Promise.all([
        queryClient.cancelQueries({ queryKey: detailKey }),
        queryClient.cancelQueries({ queryKey: listKey }),
      ]);
      const previousDetail = queryClient.getQueryData<PartnerRow>(detailKey);
      const previousList = queryClient.getQueryData<PartnerRow[]>(listKey);
      if (patchPartnerLegacyTier !== undefined) {
        queryClient.setQueryData<PartnerRow>(detailKey, (old) =>
          old ? { ...old, tier: patchPartnerLegacyTier } : old,
        );
        queryClient.setQueryData<PartnerRow[]>(listKey, (old) =>
          old?.map((p) =>
            p.id === partner_id ? { ...p, tier: patchPartnerLegacyTier } : p,
          ),
        );
      }
      return { previousDetail, previousList };
    },
    onError: (err, { partner_id }, context) => {
      if (!context) {
        toast.error(err.message ?? "Failed to assign tier");
        return;
      }
      if (context.previousDetail !== undefined) {
        queryClient.setQueryData(
          queryKeys.partners.detail(partner_id),
          context.previousDetail,
        );
      }
      if (context.previousList !== undefined) {
        queryClient.setQueryData(queryKeys.partners.list(), context.previousList);
      }
      toast.error(err.message ?? "Failed to assign tier");
    },
    onSettled: (_data, _err, { partner_id, program_id }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.partners.detail(partner_id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.partners.list() });
      queryClient.invalidateQueries({
        queryKey: queryKeys.tiers.assignmentsForProgram(program_id),
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.tiers.program(program_id) });
    },
  });
}
